import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Users2, Trophy, UserPlus, CheckCircle2, AlertCircle, Megaphone, Radio, ShieldCheck, TrendingUp } from "lucide-react";

/**
 * Analytics widget for the Admin Dashboard.
 * Hits /api/analytics/dashboard once and renders:
 *  - 6 key totals tiles
 *  - 14-day registration trend sparkline (CSS bars)
 *  - Breakdown by category + gender
 */
export default function AdminAnalytics({ role }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!["super_admin", "league_admin"].includes(role)) return;
    (async () => {
      try {
        const { data } = await api.get("/analytics/dashboard");
        setData(data);
      } catch (e) {
        setErr(e.message || "Analytics unavailable");
      }
    })();
  }, [role]);

  if (!["super_admin", "league_admin"].includes(role)) return null;
  if (err) return null; // fail-soft — Dashboard already works without it
  if (!data) return (
    <Card className="p-5 bg-[#11241b] border-white/10 mb-4 animate-pulse" data-testid="admin-analytics-loading">
      <div className="h-4 w-32 bg-white/10 rounded" />
    </Card>
  );

  const t = data.totals;
  const tiles = [
    { k: "registrations", label: "Registrations", value: t.registrations, sub: `${t.paid_registrations} paid · ${t.unpaid_registrations} unpaid`, icon: UserPlus, color: "#00A86B" },
    { k: "players",       label: "Players Pool",  value: t.players,                                                                         icon: Users2,    color: "#F4B400" },
    { k: "teams",         label: "Teams",         value: t.teams,                                                                           icon: Trophy,    color: "#E32424" },
    { k: "users",         label: "Operators",     value: t.users,        sub: `${t.pending_users} pending approval`,                        icon: ShieldCheck, color: "#7DD3FC" },
    { k: "live",          label: "Live Matches",  value: t.live_matches, sub: `${t.finished_matches} finished`,                             icon: Radio,     color: "#FF3B3B" },
    { k: "leads",         label: "Sponsor Leads", value: t.new_sponsor_leads, sub: "new in pipeline",                                       icon: Megaphone, color: "#A78BFA" },
  ];

  const trend = data.registration_trend_14d || [];
  const peak = Math.max(1, ...trend.map((d) => d.count));
  const total14 = trend.reduce((a, d) => a + d.count, 0);

  return (
    <div className="mb-6" data-testid="admin-analytics">
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">Snapshot · Last 14 days</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map((tile) => {
          const Icon = tile.icon;
          return (
            <Card key={tile.k} className="bg-[#11241b] border-white/10 p-4" data-testid={`analytics-tile-${tile.k}`}>
              <div className="flex items-center justify-between">
                <Icon className="w-4 h-4" style={{ color: tile.color }} />
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">{tile.label}</div>
              </div>
              <div className="font-anton text-3xl text-white mt-2 leading-none" data-testid={`analytics-value-${tile.k}`}>{tile.value}</div>
              {tile.sub && <div className="text-[10px] text-zinc-500 mt-1">{tile.sub}</div>}
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-[2fr_1fr] gap-3 mt-3">
        <Card className="bg-[#11241b] border-white/10 p-4" data-testid="analytics-trend">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><TrendingUp className="w-3 h-3 text-[#00A86B]" /> Registrations · 14d Trend</div>
              <div className="font-anton text-2xl text-white mt-1">{total14} <span className="text-sm text-zinc-500 font-normal">total</span></div>
            </div>
          </div>
          <div className="mt-3 flex items-end gap-1.5 h-24">
            {trend.map((d) => {
              const h = Math.round((d.count / peak) * 100);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
                  <div className="w-full rounded-t" style={{ height: `${Math.max(h, 2)}%`, background: d.count > 0 ? "#00A86B" : "rgba(255,255,255,0.05)" }} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[9px] uppercase tracking-widest text-zinc-600 mt-1">
            <span>{trend[0]?.date.slice(5)}</span>
            <span>{trend[trend.length - 1]?.date.slice(5)}</span>
          </div>
        </Card>

        <Card className="bg-[#11241b] border-white/10 p-4" data-testid="analytics-breakdowns">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">By Category</div>
          <div className="flex flex-wrap gap-2 mt-2 mb-3">
            {(data.by_category || []).length === 0 && <span className="text-xs text-zinc-600">No registrations yet</span>}
            {(data.by_category || []).map((c) => (
              <span key={c.key} className="text-[11px] uppercase tracking-widest font-bold bg-[#F4B400]/15 border border-[#F4B400]/40 text-[#F4B400] px-2 py-1 rounded" data-testid={`analytics-cat-${c.key}`}>
                {c.key}: {c.count}
              </span>
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">By Gender</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {(data.by_gender || []).length === 0 && <span className="text-xs text-zinc-600">—</span>}
            {(data.by_gender || []).map((g) => (
              <span key={g.key} className="text-[11px] uppercase tracking-widest font-bold bg-[#00A86B]/15 border border-[#00A86B]/40 text-[#00A86B] px-2 py-1 rounded" data-testid={`analytics-gender-${g.key}`}>
                {g.key}: {g.count}
              </span>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
